import { animate, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, ViewChild } from '@angular/core';
import { Customers, Orders } from '../../interfaces';
import { EditOrderDialogComponent } from '../../order-management/edit-order-dialog/edit-order-dialog.component';
import { forkJoin, Subscription } from 'rxjs';
import { DataServiceService } from '../../services/data-service.service';
import { DateTime } from 'luxon';
import { WeekCalendarComponent } from '../week-calendar/week-calendar.component';
import { Router } from '@angular/router';


interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  orderCount: number;
}

@Component({
  selector: 'app-order-calendar',
  templateUrl: './order-calendar.component.html',
  styleUrl: './order-calendar.component.scss',
  animations: [
    trigger('slideRightLeft', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('300ms ease-out', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ]),
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class OrderCalendarComponent implements OnDestroy {

  @ViewChild(EditOrderDialogComponent) editOrderDialog!: EditOrderDialogComponent;
  @ViewChild(WeekCalendarComponent) weekCalendar!: WeekCalendarComponent;

  currentDate = new Date();
  selectedDate: Date | null = null;
  calendarDays: CalendarDay[] = [];


  monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  orders: Orders[] = [];
  customers: Customers[] = [];
  showOrderDrawer: any;
  private subscriptions: Subscription[] = [];

  stateOptions: any[] = [{ label: 'Month', value: 'month' },{ label: 'Week', value: 'week' }];

  value: string = 'month';

  constructor(private dataServiceService: DataServiceService, private router: Router) { }


  ngOnInit() {
    // Subscribe to orders$ observable for real-time updates
    this.subscriptions.push(
      this.dataServiceService.orders$.subscribe(orders => {
        this.orders = orders;
      })
    );

    // Subscribe to customers
    this.subscriptions.push(
      this.dataServiceService.customers$.subscribe(customers => {
        this.customers = customers;
      })
    );

    // Initial data load
    forkJoin({
      orders: this.dataServiceService.getOrders(),
      customers: this.dataServiceService.getAllCustomers()
    }).subscribe();
  }

  isSameDate(date1: Date, date2: Date): boolean {
    return date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear();
  }

  getOrderCount(date: Date) {
    return this.getOrdersForDate(date).length;
  }

  getOrdersForDate(date: Date): Orders[] {
    return this.orders.filter(order => {
      const orderDate = new Date(order.delivery_date);
      return this.isSameDate(date, orderDate);
    });
  }

  getCustomerName(customerId: number): string {
    const customer = this.customers.find(c => c.customer_id === customerId);
    return customer ? customer.name : 'Unknown Customer';
  }


  onDateSelected(selectedDate: Date) {    
    this.selectedDate = selectedDate;
    this.showOrderDrawer = true;
  }


  previousMonth() {
    console.log('Previous Month clicked');
    this.currentDate = new Date(
      this.currentDate.getFullYear(), 
      this.currentDate.getMonth() - 1, 
      1
    );
  }

  nextMonth() {
    console.log('Next Month clicked');
    this.currentDate = new Date(
      this.currentDate.getFullYear(), 
      this.currentDate.getMonth() + 1, 
      1
    );
  }

  closeDrawer() {
    this.selectedDate = null;
    // Also update calendar to remove selection
    this.calendarDays.forEach(day => day.isSelected = false);
  }

  getCurrentMonthYear(): string {
    return `${this.monthNames[this.currentDate.getMonth()]} ${this.currentDate.getFullYear()}`;
  }

  // Week navigation methods
  previousWeek() {
    if (this.weekCalendar) {
      this.weekCalendar.previousDay();
    }
  }

  nextWeek() {
    if (this.weekCalendar) {
      this.weekCalendar.nextDay();
    }
  }

  getWeekRange(): string {
    if (this.weekCalendar && this.weekCalendar.weekDays.length > 0) {
      const firstDay = this.weekCalendar.weekDays[0];
      const lastDay = this.weekCalendar.weekDays[6];
      
      if (firstDay.getMonth() === lastDay.getMonth()) {
        return `${firstDay.toLocaleDateString('en-US', { month: 'short' })} ${firstDay.getDate()} - ${lastDay.getDate()}, ${lastDay.getFullYear()}`;
      } else {
        const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
        return `${firstDay.toLocaleDateString('en-US', options)} - ${lastDay.toLocaleDateString('en-US', options)}, ${lastDay.getFullYear()}`;
      }
    }
    return 'Week View';
  }

  toCentralTime(date: string | Date): string {
    const dt = DateTime.fromISO(date.toString(), { zone: 'utc' });
    const centralTime = dt.setZone('America/Chicago');
    // Check if the time is midnight in Central Time (meaning no specific time was selected)
    if (centralTime.hour === 0 && centralTime.minute === 0 && centralTime.second === 0) {
      return centralTime.toFormat('MM/dd/yyyy');
    }
    return centralTime.toFormat('MM/dd/yyyy h:mm a');
  }

  editOrder(order: Orders) {
    console.log('Editing order:', order);
    this.editOrderDialog.editOrder(order.order_id);
  }

  viewOrdersPage(orderId: number) {
           // Since orders are now sorted in descending order, we need to adjust the index calculation
           const orderIndex = this.orders
           .sort((a, b) => b.order_id - a.order_id) // Sort in same order as display
           .findIndex(order => order.order_id === orderId);
       const pageIndex = Math.floor(orderIndex / 10);
       
       this.router.navigate(['/orders'], { 
           queryParams: { 
               orderId: orderId, 
               pageIndex: pageIndex,
               openOrder: true,
               expand: true
           }
       });
}

  ngOnDestroy() {
    // Clean up subscriptions to prevent memory leaks
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
